import React, { useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Image } from 'expo-image';
import { X, Flag, Plus } from 'lucide-react-native';
import { router, type Href } from 'expo-router';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Button, Avatar, Skeleton, DotLabel, ReadinessMeter } from '../common';
import { Crossfade } from '../common/Crossfade';
import { useEscapeToClose } from '../common/AdaptiveSheet';
import { Post, Structure, normalizePostCategory } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { useStructuresStore } from '../../store/structuresStore';
import { usePostsStore } from '../../store/postsStore';
import { useAuthGate } from '../../hooks/useAuthGate';
import { formatRelativeTime, formatShortDate } from '../../utils/time';
import { PanelStack, usePanelStack } from '../layout/PanelStack';
import { PostDetailBody } from '../posts/PostDetailBody';

interface StructureDetailSheetProps {
  structure: Structure;
  onClose?: () => void;
  /** Desktop shell only: opens a post as a PanelStack push inside the
   *  enclosing surface (StructureDetailPanel below) instead of navigating to
   *  /post/[id] — panel-system.md Phase 2's "from the property panel" drill-
   *  in. Omit to keep the route-navigation default (mobile, and the phone
   *  BottomSheet host on web below the desktop shell). */
  onPostPress?: (post: Post) => void;
  /** Desktop dock only (StructureDetailPanel below): renders the header
   *  (address + close) fixed and everything below it in its own ScrollView,
   *  so the header stays put while the meta/activity/actions scroll under
   *  it — same "pin the header" fix as the post panel (panel-system.md
   *  review, note 4). The mobile BottomSheet host leaves this off: it sizes
   *  itself to content (enableDynamicSizing), which needs the unbounded
   *  height this prop's ScrollView would collapse. */
  scrollableBody?: boolean;
}

function StructureDetailSheetImpl({
  structure,
  onClose,
  onPostPress,
  scrollableBody = false,
}: StructureDetailSheetProps) {
  const { colors } = useTheme();
  // Web a11y parity with AdaptiveSheet: Escape closes the selection. This
  // component mounts only while a structure is selected (inline sheet and
  // DockedPanel paths alike), so mounted == open; the hook no-ops off web
  // and the shared LIFO stack keeps stacked sheets (e.g. the claim sheet)
  // closing topmost-first.
  useEscapeToClose(true, () => onClose?.());
  // Narrow selectors: bare store calls re-rendered the sheet on every
  // posts/auth store change (incl. background mapPosts pushes).
  const user = useAuthStore((s) => s.user);
  const isClaiming = useStructuresStore((s) => s.isClaiming);
  const { requireAuth } = useAuthGate();
  const structurePosts = usePostsStore((s) => s.structurePosts);
  const structurePostsStructureId = usePostsStore((s) => s.structurePostsStructureId);
  const fetchStructurePosts = usePostsStore((s) => s.fetchStructurePosts);

  const isClaimed = Boolean(structure.claimSummary?.claimedBy);
  // Both sides must exist: with no user and no claim the optional chains
  // both yield undefined and `undefined === undefined` would mark a
  // signed-out visitor as the owner.
  const isOwnedByUser = Boolean(
    user && structure.claimSummary && structure.claimSummary.claimedBy === user.id
  );
  const readinessPercent = structure.claimSummary?.progressPercent ?? null;

  // Fetch posts for this structure so we can show progress/risk counts.
  // The store memoizes by structureId — repeat opens are cheap.
  useEffect(() => {
    fetchStructurePosts(structure.id);
  }, [structure.id, fetchStructurePosts]);

  // Only trust counts if the store's currently-loaded batch matches this sheet.
  const countsMatch = structurePostsStructureId === structure.id;
  let progressCount: number | null = null;
  let riskCount: number | null = null;
  let newsCount: number | null = null;
  if (countsMatch) {
    progressCount = 0;
    riskCount = 0;
    newsCount = 0;
    for (const p of structurePosts) {
      const category = normalizePostCategory(p.category as string);
      if (category === 'progress') progressCount++;
      else if (category === 'news') newsCount++;
      else riskCount++;
    }
  }

  // Activity section derivations — only trusted when the store's loaded batch
  // matches this sheet's structureId (otherwise we'd show stale or foreign posts).
  const postsLoaded = countsMatch;
  const latestPosts: Post[] = postsLoaded ? structurePosts.slice(0, 2) : [];
  const photoPosts: Post[] = postsLoaded
    ? structurePosts.filter((p) => p.thumbnailUrl || p.imageUrl).slice(0, 4)
    : [];
  const postsCount = postsLoaded ? structurePosts.length : 0;
  const latestPost = postsLoaded ? structurePosts[0] : undefined;

  const goToCreatePost = () => {
    const returnPath = `/post/create?structureId=${structure.id}`;
    requireAuth(
      () => {
        router.push({ pathname: '/post/create', params: { structureId: structure.id } });
      },
      { message: 'Sign in to create a post', returnPath }
    );
  };

  // Structure detail is a public screen (it's the share-link landing page),
  // so viewing it is never auth-gated. The map screen keeps
  // `selectedStructure` across navigation, so the sheet is already showing
  // this structure when the user comes back — no pending-focus plumbing
  // needed.
  const handleViewDetails = () => {
    router.push(`/structure/${structure.id}` as Href);
  };

  // Claiming from the map routes into the structure detail screen so the
  // user sees the typology + insurer picker before a claim commits.
  const handleClaim = () => {
    const returnPath = `/structure/${structure.id}`;
    // Anonymous accounts pass: this only navigates to the structure page,
    // whose claim flow already forces the upgrade sheet for anon users.
    requireAuth(
      () => {
        router.push(returnPath as Href);
        onClose?.();
      },
      { message: 'Sign in to claim a property', returnPath, allowAnonymous: true }
    );
  };

  // Post detail is likewise public (share links) — reads aren't gated, only
  // interactions on that screen are. `onPostPress` (desktop shell, via
  // StructureDetailPanel below) drills in instead of navigating away.
  const handlePostPress = (post: Post) => {
    if (onPostPress) {
      onPostPress(post);
      return;
    }
    router.push(`/post/${post.id}` as Href);
  };

  // Everything below the header scrolls in its own container when docked on
  // the desktop shell (scrollableBody) — the mobile BottomSheet host passes
  // neither prop, so it renders the identical plain fragment it always has,
  // sized to content by gorhom's dynamic sizing (a ScrollView there has no
  // bounded height to scroll within and would just collapse).
  const BodyWrapper: React.ComponentType<any> = scrollableBody ? ScrollView : React.Fragment;
  const bodyWrapperProps = scrollableBody
    ? {
        style: styles.scrollBody,
        contentContainerStyle: styles.scrollBodyContent,
        showsVerticalScrollIndicator: false,
      }
    : {};

  return (
    <View style={[styles.container, scrollableBody && styles.containerBounded]}>
      {/* Header — light address title, X on the right */}
      <View style={styles.header}>
        <Text style={[styles.address, { color: colors.text }]} numberOfLines={2}>
          {structure.address || 'Unknown Address'}
        </Text>
        <TouchableOpacity
          onPress={onClose}
          style={styles.closeButton}
          accessibilityRole="button"
          accessibilityLabel="Close structure details"
          testID="structure-sheet-close">
          <X size={18} color={colors.textSecondary} />
        </TouchableOpacity>
      </View>

      <BodyWrapper {...bodyWrapperProps}>
        {/* Metadata block — Plaud's label/value lines; status and counts are
          dot-labels, not badges */}
        <View style={styles.metaBlock}>
          <Text style={[styles.metaLine, { color: colors.textSecondary }]}>
            <Text style={styles.metaLabel}>Location:</Text> {structure.city}, {structure.state}{' '}
            {structure.zip}
          </Text>
          {structure.propertyType && (
            <Text style={[styles.metaLine, { color: colors.textSecondary }]}>
              <Text style={styles.metaLabel}>Type:</Text> {structure.propertyType}
            </Text>
          )}
          <View style={styles.metaRow}>
            <Text style={[styles.metaLine, { color: colors.textSecondary }]}>
              <Text style={styles.metaLabel}>Status:</Text>
            </Text>
            {isClaimed ? (
              <DotLabel label={isOwnedByUser ? 'Your Property' : 'Claimed'} variant="progress" />
            ) : (
              <DotLabel label="Unclaimed" variant="muted" />
            )}
            {structure.source === 'user-added' && structure.verified === false && (
              <DotLabel label="Unverified" variant="partial" />
            )}
          </View>
          {progressCount !== null &&
            riskCount !== null &&
            newsCount !== null &&
            progressCount + riskCount + newsCount > 0 && (
              <View style={styles.metaRow}>
                <Text style={[styles.metaLine, { color: colors.textSecondary }]}>
                  <Text style={styles.metaLabel}>Posts:</Text>
                </Text>
                {progressCount > 0 && (
                  <DotLabel label={`${progressCount} Progress`} variant="progress" />
                )}
                {riskCount > 0 && (
                  <DotLabel
                    label={`${riskCount} ${riskCount === 1 ? 'Opportunity' : 'Opportunities'}`}
                    variant="risk"
                  />
                )}
                {newsCount > 0 && <DotLabel label={`${newsCount} News`} variant="news" />}
              </View>
            )}
        </View>

        {/* Readiness meter */}
        {readinessPercent !== null && (
          <ReadinessMeter percent={readinessPercent} style={styles.meter} />
        )}

        {/* Activity */}
        <View style={[styles.activitySection, { borderTopColor: colors.divider }]}>
          <View style={styles.activityHeader}>
            <View style={[styles.activityTitleWrap, { borderBottomColor: colors.text }]}>
              <Text style={[styles.activityTitle, { color: colors.text }]}>Activity</Text>
            </View>
            {postsLoaded && postsCount > 0 && latestPost && (
              <Text style={[styles.activitySignal, { color: colors.textTertiary }]}>
                {postsCount} {postsCount === 1 ? 'post' : 'posts'} · last{' '}
                {formatRelativeTime(latestPost.createdAt, { suffix: 'ago' })}
              </Text>
            )}
          </View>

          <Crossfade
            loading={!postsLoaded}
            skeleton={
              <View style={styles.photoStrip} testID="structure-panel-skeleton">
                {[0, 1, 2].map((i) => (
                  <Skeleton
                    static
                    key={i}
                    width={72}
                    height={72}
                    borderRadius={BORDER_RADIUS.md}
                    style={{ marginRight: SPACING.sm }}
                  />
                ))}
              </View>
            }>
            <>
              {postsLoaded && photoPosts.length > 0 && (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.photoStrip}>
                  {photoPosts.map((p) => (
                    <TouchableOpacity
                      key={p.id}
                      testID={`structure-post-photo-${p.id}`}
                      onPress={() => handlePostPress(p)}
                      activeOpacity={0.85}
                      style={{ marginRight: SPACING.sm }}>
                      <Image
                        source={p.thumbnailUrl || p.imageUrl}
                        style={[styles.photoThumb, { backgroundColor: colors.avatarPlaceholder }]}
                        contentFit="cover"
                        cachePolicy="memory-disk"
                        transition={150}
                      />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}

              {postsLoaded && latestPosts.length > 0 && (
                <View style={styles.postRows}>
                  {latestPosts.map((p) => {
                    const postCategory = normalizePostCategory(p.category as string);
                    const categoryLabel =
                      postCategory === 'progress'
                        ? 'Progress'
                        : postCategory === 'news'
                          ? 'News'
                          : 'Opportunity';
                    const categoryColor =
                      postCategory === 'progress'
                        ? colors.progress
                        : postCategory === 'news'
                          ? colors.news
                          : colors.opportunity;
                    return (
                      <TouchableOpacity
                        key={p.id}
                        testID={`structure-post-row-${p.id}`}
                        style={styles.postRow}
                        onPress={() => handlePostPress(p)}
                        activeOpacity={0.7}>
                        <Avatar size={28} name={p.authorName} imageUrl={p.authorAvatarUrl} />
                        <View style={styles.postRowBody}>
                          <Text
                            style={[styles.postRowMeta, { color: colors.textSecondary }]}
                            numberOfLines={1}>
                            <Text style={{ color: colors.text, ...withWeight({}, 'medium') }}>
                              {p.authorName || 'Someone'}
                            </Text>
                            {'  ·  '}
                            <Text style={{ color: categoryColor }}>{categoryLabel}</Text>
                            {'  ·  '}
                            {formatRelativeTime(p.createdAt, { suffix: 'ago' })}
                          </Text>
                          {!!p.caption && (
                            <Text
                              style={[styles.postRowCaption, { color: colors.text }]}
                              numberOfLines={1}>
                              {p.caption}
                            </Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {postsLoaded && postsCount === 0 && (
                <View style={styles.emptyRow}>
                  <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
                    No posts yet.
                  </Text>
                  {isOwnedByUser && (
                    <TouchableOpacity onPress={goToCreatePost} activeOpacity={0.7}>
                      <Text style={[styles.emptyLink, { color: colors.primary }]}>
                        Be the first to post
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </>
          </Crossfade>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          <Button
            title="View details"
            onPress={handleViewDetails}
            variant="primary"
            size="md"
            style={styles.primaryAction}
          />
          {!isClaimed && (
            <Button
              title="Claim Property"
              onPress={handleClaim}
              variant="outline"
              size="md"
              leftIcon={Flag}
              loading={isClaiming}
              style={styles.secondaryAction}
            />
          )}
          {isOwnedByUser && (
            <Button
              title="Add Observation"
              onPress={() => {
                router.push({
                  pathname: '/post/create',
                  params: { structureId: structure.id },
                });
              }}
              variant="outline"
              size="md"
              leftIcon={Plus}
              style={styles.secondaryAction}
            />
          )}
        </View>
      </BodyWrapper>
    </View>
  );
}

export const StructureDetailSheet = React.memo(StructureDetailSheetImpl);

// Category + date ("Progress · Jul 24"), not the post's location text: the
// property is already the parent here, so repeating its address added
// nothing — and a typo'd address ("11 Poliqie") showed up as the title
// (panel-system.md review, note 8).
function postPanelTitle(post: Post): string {
  const category = normalizePostCategory(post.category);
  if (category === 'news') return post.title || 'News post';
  const label = category === 'progress' ? 'Progress' : 'Opportunity';
  const date = formatShortDate(post.createdAt);
  return date ? `${label} · ${date}` : label;
}

interface StructureDetailPanelProps {
  structure: Structure;
  onClose?: () => void;
}

function StructureDetailPanelRoot({ structure, onClose }: StructureDetailPanelProps) {
  const { push } = usePanelStack();
  const handleOpenPost = useCallback(
    (post: Post) => {
      push({
        key: `post-${post.id}`,
        title: postPanelTitle(post),
        // PostDetailBody renders plain content, no ScrollView of its own
        // (every other host supplies one) — this is this drill-in's, so its
        // own overflow scrolls under PanelStack's fixed back/title header
        // rather than the panel just clipping it.
        render: () => (
          <ScrollView
            style={styles.scrollBody}
            contentContainerStyle={styles.scrollBodyContent}
            showsVerticalScrollIndicator={false}>
            <PostDetailBody postId={post.id} />
          </ScrollView>
        ),
      });
    },
    [push]
  );
  return (
    <StructureDetailSheet
      structure={structure}
      onClose={onClose}
      onPostPress={handleOpenPost}
      scrollableBody
    />
  );
}

/**
 * Desktop-shell host for the right-docked structure panel
 * (app/(tabs)/index.tsx): wraps StructureDetailSheet in a PanelStack so a
 * post opened from it (the activity strip/rows) is a drill-in — back arrow
 * and Escape both pop to the property — rather than a second dock
 * (panel-system.md Phase 2, "from the property panel"). The phone/tablet
 * BottomSheet host below the desktop shell keeps plain StructureDetailSheet,
 * whose default `onPostPress` (route navigation) is unaffected.
 */
export function StructureDetailPanel({ structure, onClose }: StructureDetailPanelProps) {
  return (
    <PanelStack
      root={<StructureDetailPanelRoot structure={structure} onClose={onClose} />}
      historyKey="structure-post"
    />
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: SPACING.sm,
    paddingBottom: SPACING.lg,
  },
  // Desktop dock only (scrollableBody) — a real flex height so the
  // ScrollView below the header has something bounded to scroll within
  // instead of growing to its content, which is what mobile's dynamic
  // sizing needs the plain fragment for.
  containerBounded: {
    flex: 1,
    minHeight: 0,
    paddingBottom: 0,
  },
  scrollBody: {
    flex: 1,
  },
  scrollBodyContent: {
    paddingBottom: SPACING.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: SPACING.md,
  },
  // The light address register: h3 scale with the light face
  address: {
    ...TYPOGRAPHY.h3,
    fontFamily: TYPOGRAPHY.screenTitle.fontFamily,
    letterSpacing: -0.24,
    flex: 1,
  },
  closeButton: {
    padding: SPACING.xs,
    marginTop: -SPACING.xs,
  },
  metaBlock: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
    paddingBottom: SPACING.xs,
    gap: SPACING.xs,
  },
  metaLine: {
    ...TYPOGRAPHY.metadata,
  },
  metaLabel: {
    fontFamily: TYPOGRAPHY.label.fontFamily, // medium label, quiet value
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  meter: {
    marginHorizontal: SPACING.md,
    marginTop: SPACING.sm,
    marginBottom: SPACING.xs,
  },
  activitySection: {
    borderTopWidth: StyleSheet.hairlineWidth,
    marginHorizontal: SPACING.md,
    marginTop: SPACING.sm,
    paddingTop: SPACING.smd,
    paddingBottom: SPACING.sm,
  },
  activityHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: SPACING.smd,
  },
  // Underlined section word (mockup's .ovl b)
  activityTitleWrap: {
    borderBottomWidth: 2,
    paddingBottom: 3,
    alignSelf: 'flex-start',
  },
  activityTitle: {
    ...TYPOGRAPHY.label,
    fontFamily: TYPOGRAPHY.h4.fontFamily,
  },
  activitySignal: {
    ...TYPOGRAPHY.bodySmall,
    flexShrink: 1,
    marginLeft: SPACING.sm,
  },
  photoStrip: {
    flexDirection: 'row',
    paddingRight: SPACING.md,
  },
  photoThumb: {
    width: 72,
    height: 72,
    borderRadius: BORDER_RADIUS.md,
  },
  postRows: {
    marginTop: SPACING.smd,
  },
  postRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.xs,
  },
  postRowBody: {
    flex: 1,
    marginLeft: SPACING.sm,
  },
  postRowMeta: {
    ...TYPOGRAPHY.bodySmall,
  },
  postRowCaption: {
    ...TYPOGRAPHY.body,
    marginTop: 2,
  },
  emptyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  emptyText: {
    ...TYPOGRAPHY.body,
  },
  emptyLink: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  actions: {
    flexDirection: 'column',
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.smd,
    gap: SPACING.sm,
  },
  primaryAction: {
    alignSelf: 'stretch',
  },
  secondaryAction: {
    alignSelf: 'stretch',
  },
});
